import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, RefreshCw, Trash2, Inbox, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LogoMark } from "@/components/landing/Logo";
import { API } from "@/constants/site";

const STATUSES = [
  { key: "all", label: "All" },
  { key: "new", label: "New" },
  { key: "contacted", label: "Contacted" },
  { key: "closed", label: "Closed" },
];

const STATUS_STYLES = {
  new: "bg-amber-100 text-amber-800 border-amber-200",
  contacted: "bg-blue-100 text-blue-800 border-blue-200",
  closed: "bg-emerald-100 text-emerald-800 border-emerald-200",
};

const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47c-.29 1.48-1.14 2.73-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z" />
    <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09C3.26 21.3 7.31 24 12 24z" />
    <path fill="#FBBC05" d="M5.27 14.29c-.25-.72-.38-1.49-.38-2.29s.14-1.57.38-2.29V6.62H1.29C.47 8.24 0 10.06 0 12s.47 3.76 1.29 5.38l3.98-3.09z" />
    <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.7 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z" />
  </svg>
);

export default function Admin() {
  const location = useLocation();
  const [token, setToken] = useState(() => localStorage.getItem("orion_admin_token") || "");
  const [sessionUser, setSessionUser] = useState(location.state?.user || null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [enquiries, setEnquiries] = useState(null);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false);

  const authed = Boolean(token) || Boolean(sessionUser);

  const authConfig = () => ({
    withCredentials: true,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  const fetchEnquiries = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/enquiries`, authConfig());
      setEnquiries(data);
    } catch {
      setToken("");
      setSessionUser(null);
      localStorage.removeItem("orion_admin_token");
      toast.error("Session expired. Please sign in again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token || sessionUser) {
      fetchEnquiries();
      return;
    }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setSessionUser(data);
      } catch {
        /* not signed in — show login */
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, sessionUser]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("orion_admin_token", data.access_token);
      setToken(data.access_token);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {
      /* best effort */
    }
    localStorage.removeItem("orion_admin_token");
    setToken("");
    setSessionUser(null);
    setEnquiries(null);
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/enquiries/${id}`, authConfig());
      setEnquiries((list) => list.filter((x) => x.id !== id));
      toast.success("Enquiry deleted.");
    } catch {
      toast.error("Could not delete enquiry.");
    }
  };

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/enquiries/${id}`, { status }, authConfig());
      setEnquiries((list) => list.map((x) => (x.id === id ? { ...x, status } : x)));
      toast.success(`Marked as ${status}.`);
    } catch {
      toast.error("Could not update status.");
    }
  };

  if (!authed) {
    return (
      <div data-testid="admin-login-page" className="min-h-screen bg-[#0B192C] flex items-center justify-center px-5">
        <form data-testid="admin-login-form" onSubmit={login}
          className="w-full max-w-md bg-white rounded-2xl p-8 sm:p-10 shadow-2xl">
          <div className="flex items-center gap-3">
            <LogoMark className="w-10 h-10" />
            <div>
              <p className="font-heading font-bold text-xl text-[#0B192C]">Orion Edcon</p>
              <p className="text-xs uppercase tracking-widest text-amber-700 font-semibold">Admin Console</p>
            </div>
          </div>
          <div className="mt-8 space-y-4">
            <Input data-testid="admin-login-email-input" type="email" placeholder="Admin email" required
              value={email} onChange={(e) => setEmail(e.target.value)} className="h-12" />
            <Input data-testid="admin-login-password-input" type="password" placeholder="Password" required
              value={password} onChange={(e) => setPassword(e.target.value)} className="h-12" />
          </div>
          {error && <p data-testid="admin-login-error" className="text-sm text-red-600 mt-3">{error}</p>}
          <Button data-testid="admin-login-submit-btn" type="submit" disabled={loading}
            className="w-full h-12 mt-6 rounded-full bg-[#0B192C] hover:bg-[#1E3E62] text-white font-semibold">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Sign In"}
          </Button>
          <div className="flex items-center gap-3 mt-5">
            <span className="h-px flex-1 bg-slate-200" />
            <span className="text-xs text-slate-400 uppercase tracking-wider">or</span>
            <span className="h-px flex-1 bg-slate-200" />
          </div>
          <button data-testid="google-login-btn" type="button" onClick={googleLogin}
            className="w-full h-12 mt-5 rounded-full border-2 border-slate-200 bg-white text-slate-700 font-semibold hover:border-[#0B192C] transition-colors flex items-center justify-center gap-2.5">
            <GoogleIcon /> Continue with Google
          </button>
          <p className="text-xs text-slate-400 text-center mt-3">Google sign-in is restricted to authorised accounts.</p>
          <a href="/" data-testid="admin-back-link" className="block text-center text-sm text-slate-500 hover:text-amber-700 mt-4">
            ← Back to website
          </a>
        </form>
      </div>
    );
  }

  const counts = STATUSES.reduce((acc, s) => {
    acc[s.key] = s.key === "all" ? (enquiries?.length ?? 0) : (enquiries?.filter((q) => (q.status || "new") === s.key).length ?? 0);
    return acc;
  }, {});
  const visible = enquiries?.filter((q) => filter === "all" || (q.status || "new") === filter);

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#F8FAFC]">
      <header className="bg-[#0B192C] text-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <LogoMark className="w-7 h-7" dark />
            <span className="font-heading font-bold text-lg">Enquiries Console</span>
            {sessionUser?.email && <span data-testid="admin-signed-in-email" className="hidden sm:inline text-xs text-slate-400 ml-2">{sessionUser.email}</span>}
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="admin-refresh-btn" onClick={fetchEnquiries}
              className="flex items-center gap-1.5 text-sm text-slate-300 hover:text-white transition-colors">
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
            <button data-testid="admin-logout-btn" onClick={logout}
              className="flex items-center gap-1.5 text-sm bg-white/10 hover:bg-white/20 rounded-full px-4 py-2 transition-colors">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-5 lg:px-8 py-10">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#0B192C]">
          Enquiries {enquiries && <span data-testid="enquiry-count" className="text-amber-700">({enquiries.length})</span>}
        </h1>
        <div data-testid="status-filter-tabs" className="flex flex-wrap gap-2 mt-6">
          {STATUSES.map((s) => (
            <button key={s.key} data-testid={`filter-${s.key}`} onClick={() => setFilter(s.key)}
              className={`rounded-full px-4 py-1.5 text-sm font-semibold border transition-colors ${
                filter === s.key ? "bg-[#0B192C] text-white border-[#0B192C]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
              }`}>
              {s.label} <span className="opacity-70">({counts[s.key]})</span>
            </button>
          ))}
        </div>
        {loading && !enquiries ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-amber-600" /></div>
        ) : visible && visible.length === 0 ? (
          <div data-testid="enquiries-empty" className="text-center py-20 text-slate-500">
            <Inbox className="w-10 h-10 mx-auto text-slate-300" />
            <p className="mt-3">{filter === "all" ? "No enquiries yet. New submissions will appear here." : `No ${filter} enquiries.`}</p>
          </div>
        ) : (
          <div className="mt-8 space-y-4">
            {visible?.map((q) => (
              <article key={q.id} data-testid={`enquiry-row-${q.id}`}
                className="bg-white border border-slate-200 rounded-xl p-5 sm:p-6">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold text-[#0B192C]">{q.name}</p>
                    <p className="text-sm text-slate-500">{q.email} · {q.phone}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Select value={q.status || "new"} onValueChange={(v) => setStatus(q.id, v)}>
                      <SelectTrigger data-testid={`enquiry-status-${q.id}`}
                        className={`h-8 w-[130px] text-xs font-semibold border ${STATUS_STYLES[q.status || "new"]}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new">New</SelectItem>
                        <SelectItem value="contacted">Contacted</SelectItem>
                        <SelectItem value="closed">Closed</SelectItem>
                      </SelectContent>
                    </Select>
                    <span className="text-xs font-semibold uppercase tracking-wider bg-amber-100 text-amber-800 rounded-full px-3 py-1">{q.service}</span>
                    <button data-testid={`enquiry-delete-${q.id}`} onClick={() => remove(q.id)}
                      className="text-slate-400 hover:text-red-600 transition-colors" aria-label="Delete enquiry">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm text-slate-600">
                  <span><span className="font-medium text-slate-500">Destination:</span> {q.destination || "—"}</span>
                  <span><span className="font-medium text-slate-500">Received:</span> {q.created_at ? new Date(q.created_at).toLocaleString() : "—"}</span>
                </div>
                {q.message && <p className="mt-3 text-sm text-slate-600 bg-[#F8FAFC] border border-slate-100 rounded-lg p-3">{q.message}</p>}
              </article>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
